import React from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import { VARIATION_FIELDS, isVariationFilled } from '../config';

const Field = ({ label, value }) =>
  value ? (
    <div className="text-xs">
      <span className="font-semibold text-slate-500">{label}: </span>
      <span className="whitespace-pre-wrap break-words text-slate-700">{value}</span>
    </div>
  ) : null;

/**
 * Read-only list of the writer's current drafts — straight from
 * `detail.variations`. There is no `/versions` endpoint on this panel and no
 * rollback history server-side: this is the live array, not a timeline, so
 * it is never mixed with `logs` and never fabricates a version number that
 * would imply history the backend doesn't keep.
 */
const DraftsList = ({ variations, onOpenSubmit, canSubmitDrafts }) => {
  const list = Array.isArray(variations) ? variations.filter(isVariationFilled) : [];

  if (!list.length) {
    return (
      <div className="px-4 py-10 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
          <SlIcon
            library="lucide"
            name="file-text"
            className="text-slate-400"
            style={{ fontSize: '22px' }}
          />
        </div>
        <p className="text-sm font-medium text-slate-700">No drafts submitted yet</p>
        <p className="mx-auto mt-1 max-w-xs text-xs text-slate-500">
          Write a headline, description or link text and submit it — it will show up here.
        </p>
        {canSubmitDrafts && typeof onOpenSubmit === 'function' && (
          <button
            type="button"
            onClick={onOpenSubmit}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700"
          >
            <SlIcon library="lucide" name="pencil-line" style={{ fontSize: '14px' }} />
            Write your first draft
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        {list.length} draft{list.length === 1 ? '' : 's'} currently submitted
      </p>
      <ol className="space-y-3">
        {list.map((variation, i) => (
          <li key={i} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">Draft {i + 1}</span>
              <span className="flex items-center gap-1">
                {VARIATION_FIELDS.map((f) => {
                  const filled = !!String(variation?.[f.key] || '').trim();
                  return (
                    <span
                      key={f.key}
                      title={`${f.label}${filled ? ' set' : ' empty'}`}
                      className={`h-1.5 w-1.5 rounded-full ${filled ? 'bg-sky-500' : 'bg-slate-200'}`}
                    />
                  );
                })}
              </span>
            </div>
            <div className="space-y-1">
              {VARIATION_FIELDS.map((f) => (
                <Field key={f.key} label={f.label} value={variation?.[f.key]} />
              ))}
            </div>
          </li>
        ))}
      </ol>
      {canSubmitDrafts && typeof onOpenSubmit === 'function' && (
        <button
          type="button"
          onClick={onOpenSubmit}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-100"
        >
          <SlIcon library="lucide" name="pencil-line" style={{ fontSize: '14px' }} />
          Edit drafts
        </button>
      )}
    </div>
  );
};

export default DraftsList;
